import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/auth/slice";

import supportChatService from "../../../services/supportChatService";
import supportService from "../../../api/services/support";
import {
  FiMessageCircle,
  FiUser,
  FiSend,
  FiRefreshCw,
  FiMail,
  FiX,
  FiMoreVertical,
  FiArchive,
} from "react-icons/fi";
import {
  Header,
  HeaderTitle,
  HeaderStats,
  StatCard,
  StatNumber,
  StatLabel,
  MainContent,
  ChatList,
  ChatListHeader,
  ChatItem,
  ChatPreview,
  ChatUser,
  ChatLastMessage,
  ChatTime,
  ChatStatus,
  ChatArea,
  ChatHeader,
  ChatUserInfo,
  ChatUserName,
  ChatUserEmail,
  ChatActions,
  ActionButton,
  MessagesContainer,
  Message,
  MessageContent,
  MessageTime,
  MessageSender,
  ChatInput,
  InputContainer,
  SendButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  TypingIndicator,
  OnlineIndicator,
  QuickResponses,
  QuickResponseButton,
  ChatDropdown,
  DropdownItem,
} from "./AdminSupportPage.styles";
import AdminContainer from "../../../components/utils/AdminContainer";

const AdminSupportPage = () => {
  const user = useSelector(selectUser);
  const [activeChatId, setActiveChatId] = useState(null);
  const [message, setMessage] = useState("");
  const [showDropdown, setShowDropdown] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const messagesEndRef = useRef(null);
  const previousActiveChatId = useRef(null);

  // Initialize admin connection and load chat sessions
  useEffect(() => {
    if (user && user.role === "admin") {
      // Setup support chat service callbacks for admin
      supportChatService.setCallbacks({
        onConnect: () => {
          setIsConnected(true);
          supportChatService.requestAdminStatus();
        },
        onDisconnect: () => {
          setIsConnected(false);
        },

        onMessage: handleIncomingMessage,
        onTyping: handleTypingIndicator,
        onActiveSessions: (sessions) => {
          transformAndSetSessions(sessions);
        },
        onNewChatSession: (sessionData) => {
          handleNewChatSession(sessionData);
        },
        onSessionUpdated: handleSessionUpdated,
        onSessionEnded: handleSessionEnded,
        onSessionJoined: handleSessionJoined,
        onError: handleChatError,
        onOnlineUsers: (data) => {
          // setOnlineUsers(data.users || []);
        },
        onUserOnline: (data) => {
          // setOnlineUsers((prev) => {
          //   const exists = prev.some((u) => u.userId === data.userId);
          //   if (!exists) {
          //     return [...prev, data];
          //   }
          //   return prev;
          // });

          // Update chat sessions to reflect user online status
          setChatSessions((prev) =>
            prev.map((chat) => {
              // If this session belongs to the user who came online
              if (chat.user.id === data.userId) {
                return { ...chat, isOnline: true };
              }
              return chat;
            })
          );
        },
        onUserOffline: (data) => {
          // setOnlineUsers((prev) =>
          //   prev.filter((u) => u.userId !== data.userId)
          // );

          // Update chat sessions to reflect user offline status
          setChatSessions((prev) =>
            prev.map((chat) => {
              // If this session belongs to the user who went offline
              if (chat.user.id === data.userId) {
                return { ...chat, isOnline: false };
              }
              return chat;
            })
          );
        },
      });

      // Check if admin is already connected via AdminContainer
      if (!supportChatService.isSocketConnected()) {
        try {
          supportChatService.connect({
            id: user.id,
            userType: "admin",
            username: user.username || user.name,
            email: user.email,
          });
        } catch (error) {
          console.error("AdminSupportPage: Connection failed:", error);
        }
      } else {
      }
    }

    return () => {
      // AdminContainer now manages the connection lifecycle
      // No need to disconnect here
    };
  }, [user]);

  // Transform Socket.IO session data to frontend format
  const transformAndSetSessions = (sessions) => {
    if (!sessions || !Array.isArray(sessions)) {
      setChatSessions([]);
      return;
    }
    const transformedSessions = sessions.map((session) => ({
      id: session.id || session.sessionId,
      user: {
        id: session.userId, // Add user ID for matching
        name: session.bandUser?.username || "Unknown User",
        email: session.bandUser?.email || "Email not available",
        bandName: "Band User", // Generic label
        avatar: null,
      },
      lastMessage: session.lastMessage?.text || "No messages yet",
      timestamp: new Date(session.createdAt),
      status: session.isActive ? "active" : "inactive",
      isOnline: session.isBandUserOnline || false, // Add online status
      unreadCount: 0, // Start with 0 unread
      isTyping: false,
      messages: session.messages
        ? session.messages.map((msg) => ({
            id: msg.id,
            text: msg.text,
            sender: msg.sender,
            timestamp: new Date(msg.timestamp),
          }))
        : [],
      messageCount: session.messageCount || 0,
    }));
    setChatSessions(transformedSessions);
  };

  // This function is kept for backward compatibility but not used with Socket.IO
  const loadChatSessions = async () => {
    // No longer needed with Socket.IO - sessions come automatically
  };

  // Handle incoming messages from WebSocket
  const handleIncomingMessage = (data) => {
    const { sessionId, message } = data;

    setChatSessions((prev) =>
      prev.map((chat) => {
        if (chat.id !== sessionId) return chat;

        // Check if this message already exists to prevent duplicates
        const messageExists = chat.messages.some(
          (msg) => msg.id === message.id
        );
        if (messageExists) {
          return chat;
        }

        const newMessage = {
          id: message.id,
          text: message.text,
          sender: message.sender,
          timestamp: new Date(message.timestamp),
        };

        // Only increment unread count if:
        // 1. Message is NOT from admin
        // 2. Admin is NOT currently viewing this session
        const shouldIncrementUnread =
          message.sender !== "admin" && activeChatId !== sessionId;

        return {
          ...chat,
          messages: [...chat.messages, newMessage],
          lastMessage: message.text,
          timestamp: new Date(message.timestamp),
          unreadCount: shouldIncrementUnread
            ? chat.unreadCount + 1
            : chat.unreadCount,
        };
      })
    );
  };

  // Handle typing indicators
  const handleTypingIndicator = (data) => {
    const { sessionId, userType, isTyping } = data;
    setChatSessions((prev) =>
      prev.map(
        (chat) =>
          chat.id === sessionId
            ? {
                ...chat,
                isTyping: isTyping && userType === "band", // Show typing only when band user is typing
              }
            : { ...chat, isTyping: false } // Clear typing for other chats
      )
    );
  };

  // Handle chat errors
  const handleChatError = (error) => {
    console.error("Admin support chat error:", error);
  };

  // Handle new chat session assigned to admin
  const handleNewChatSession = (sessionData) => {
    // Add new session to existing sessions
    const newSession = {
      id: sessionData.sessionId,
      user: {
        name: sessionData.userName,
        email: sessionData.userEmail || "Email not available",
        bandName: "Band User",
        avatar: null,
      },
      lastMessage: sessionData.lastMessage?.text || "New session started",
      timestamp: new Date(sessionData.timestamp),
      status: "active",
      unreadCount: 0, // Start with 0 - first message will increment this via handleIncomingMessage
      isTyping: false,
      messages: sessionData.messages || [],
      messageCount: sessionData.messageCount || 0,
    };

    setChatSessions((prev) => [newSession, ...prev]);
  };

  // Handle session updated
  const handleSessionUpdated = (updateData) => {
    setChatSessions((prev) =>
      prev.map((session) => {
        if (session.id !== updateData.sessionId) return session;

        return {
          ...session,
          lastMessage: updateData.lastMessage?.text || session.lastMessage,
          messageCount: updateData.messageCount || session.messageCount,
          // Don't increment unread count here - it's handled in handleIncomingMessage
          unreadCount: session.unreadCount,
        };
      })
    );
  };

  // Handle session ended
  const handleSessionEnded = (data) => {
    setChatSessions((prev) =>
      prev.filter((session) => session.id !== data.sessionId)
    );
    if (activeChatId === data.sessionId) {
      setActiveChatId(null);
    }
  };

  // Handle session joined - load messages from server
  const handleSessionJoined = (data) => {
    const { sessionId, messages } = data;

    setChatSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              messages: (messages || []).map((msg) => ({
                id: msg.id,
                text: msg.text,
                sender: msg.sender,
                timestamp: new Date(msg.timestamp),
              })),
              unreadCount: 0, // Reset unread since admin is viewing
            }
          : session
      )
    );
  };

  const quickResponses = [
    "Zdravo! Kako mogu da vam pomognem?",
    "Hvala vam na strpljenju. Proveraću to za vas.",
    "Možete li mi poslati screenshot greške?",
    "Problem je rešen. Da li vam je sada sve u redu?",
    "Za dodatne informacije, molimo kontaktirajte nas na podrska@mrc.rs",
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeChatId, chatSessions]);

  // Clear unread count when admin clicks on a chat or focuses input
  const clearUnreadCount = (sessionId) => {
    setChatSessions((prev) =>
      prev.map((chat) =>
        chat.id === sessionId ? { ...chat, unreadCount: 0 } : chat
      )
    );
  };

  // Join active chat room when selecting a chat
  useEffect(() => {
    if (activeChatId && isConnected) {
      supportChatService.setSessionId(activeChatId);
      supportChatService.joinSession(activeChatId);

      // Clear unread count when joining session
      clearUnreadCount(activeChatId);
    }

    // Store previous active chat ID for comparison
    previousActiveChatId.current = activeChatId;
  }, [activeChatId, isConnected]);

  const handleSendMessage = () => {
    if (!message.trim() || !activeChatId || !isConnected) return;

    const messageText = message;
    setMessage("");

    // Don't add message locally - let it come from server to avoid duplicates
    try {
      // Send message via Socket.IO
      supportChatService.sendMessage(messageText);

      // Stop typing after sending message
      supportChatService.sendTyping(false);
    } catch (error) {
      console.error("Failed to send message:", error);
      // If error, restore the message text
      setMessage(messageText);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChatSelection = async (chatId) => {
    setActiveChatId(chatId);

    // Mark session as read when admin clicks on it
    try {
      await supportService.markSessionAsRead(chatId);

      // Refresh unread count in sidebar
    } catch (error) {
      console.error("Error marking session as read:", error);
    }
  };

  // Handle input focus - also clear unread count
  const handleInputFocus = () => {
    if (activeChatId) {
      clearUnreadCount(activeChatId);
      supportChatService.sendTyping(true);
    }
  };

  const handleInputBlur = () => {
    supportChatService.sendTyping(false);
  };

  const formatTime = (date) => {
    if (!date) return "N/A";

    const chatDate = new Date(date);
    if (isNaN(chatDate.getTime())) return "N/A";

    const now = new Date();
    const diff = now - chatDate;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return "Upravo";
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return chatDate.toLocaleDateString("sr-Latn-RS");
  };

  const activeChat = chatSessions.find((chat) => chat.id === activeChatId);
  const activeChats = chatSessions.filter(
    (chat) => chat.status === "active"
  ).length;
  const waitingChats = chatSessions.filter(
    (chat) => chat.status === "waiting"
  ).length;

  // Calculate unread messages from local state
  const totalUnread = 0; // This was hardcoded before

  const handleQuickResponse = (response) => {
    setMessage(response);
  };

  const handleChatAction = (action, chatId) => {
    switch (action) {
      case "close":
        // End session via Socket.IO
        if (isConnected) {
          supportChatService.endSession(chatId);
        }
        break;

      default:
        break;
    }
    setShowDropdown(null);
  };

  return (
    <AdminContainer>
      <Header>
        <HeaderTitle>
          <FiMessageCircle />
          Support Chat Dashboard
        </HeaderTitle>
        <HeaderStats>
          <StatCard>
            <StatNumber>{activeChats}</StatNumber>
            <StatLabel>Aktivni Chat-ovi</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{waitingChats}</StatNumber>
            <StatLabel>Na Čekanju</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{totalUnread}</StatNumber>
            <StatLabel>Nepročitane</StatLabel>
          </StatCard>
        </HeaderStats>
      </Header>

      <MainContent>
        {/* Chat List */}
        <ChatList>
          <ChatListHeader>
            <h3>
              Chat Sesije{" "}
              {!isConnected && (
                <span style={{ color: "#f39c12", fontSize: "0.8rem" }}>
                  (Offline)
                </span>
              )}
            </h3>
            <ActionButton onClick={loadChatSessions}>
              <FiRefreshCw />
            </ActionButton>
          </ChatListHeader>

          {!chatSessions || chatSessions.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <FiMessageCircle />
              </EmptyStateIcon>
              <EmptyStateText>Nema aktivnih chat sesija</EmptyStateText>
            </EmptyState>
          ) : (
            chatSessions.map((chat) => (
              <ChatItem
                key={chat.id}
                $active={activeChatId === chat.id}
                $unread={chat.unreadCount > 0}
                onClick={() => handleChatSelection(chat.id)}
              >
                <ChatPreview>
                  <ChatUser>
                    <div className="avatar">
                      <FiUser />
                    </div>
                    <div className="info">
                      <ChatUserName>{chat.user.name}</ChatUserName>
                      <div className="band-name">{chat.user.bandName}</div>
                    </div>
                    <OnlineIndicator $online={chat.isOnline} />
                  </ChatUser>
                  <ChatLastMessage>{chat.lastMessage}</ChatLastMessage>
                  <ChatTime>{formatTime(chat.timestamp)}</ChatTime>
                </ChatPreview>
                {/* TODO: Implement per-chat unread count */}
                {false && chat.unreadCount > 0 && (
                  <ChatStatus>{chat.unreadCount}</ChatStatus>
                )}
              </ChatItem>
            ))
          )}
        </ChatList>

        {/* Chat Area */}
        <ChatArea>
          {activeChat ? (
            <>
              <ChatHeader>
                <ChatUserInfo>
                  <div className="avatar">
                    <FiUser />
                  </div>
                  <div>
                    <ChatUserName>{activeChat.user.name}</ChatUserName>
                    <ChatUserEmail>{activeChat.user.email}</ChatUserEmail>
                  </div>
                </ChatUserInfo>
                <ChatActions>
                  {activeChat.user.email && (
                    <ActionButton
                      onClick={() => {
                        window.location.href = `mailto:${activeChat.user.email}`;
                      }}
                    >
                      <FiMail />
                    </ActionButton>
                  )}

                  <ActionButton
                    onClick={() =>
                      setShowDropdown(
                        showDropdown === activeChatId ? null : activeChatId
                      )
                    }
                  >
                    <FiMoreVertical />
                  </ActionButton>
                  {showDropdown === activeChatId && (
                    <ChatDropdown>
                      <DropdownItem onClick={() => setActiveChatId(null)}>
                        <FiX /> Zatvori Chat
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => handleChatAction("close", activeChatId)}
                      >
                        <FiArchive /> Završi Sesiju
                      </DropdownItem>
                    </ChatDropdown>
                  )}
                </ChatActions>
              </ChatHeader>

              <MessagesContainer>
                {activeChat.messages?.map((msg) => (
                  <Message key={msg.id} $isAdmin={msg.sender === "admin"}>
                    <MessageSender>
                      {msg.sender === "admin"
                        ? user?.username
                        : activeChat.user.name}
                    </MessageSender>
                    <MessageContent $isAdmin={msg.sender === "admin"}>
                      {msg.text}
                    </MessageContent>
                    <MessageTime>{formatTime(msg.timestamp)}</MessageTime>
                  </Message>
                ))}
                {activeChat.isTyping && (
                  <TypingIndicator>
                    <div className="dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </TypingIndicator>
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              <QuickResponses>
                {quickResponses.map((response, index) => (
                  <QuickResponseButton
                    key={index}
                    onClick={() => handleQuickResponse(response)}
                  >
                    {response}
                  </QuickResponseButton>
                ))}
              </QuickResponses>

              <InputContainer>
                <ChatInput
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder="Ukucajte vašu poruku..."
                  rows="1"
                />
                <SendButton
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                >
                  <FiSend size={14} />
                </SendButton>
              </InputContainer>
            </>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <FiMessageCircle />
              </EmptyStateIcon>
              <EmptyStateText>
                Odaberite chat sesiju da biste počeli konverzaciju
              </EmptyStateText>
            </EmptyState>
          )}
        </ChatArea>
      </MainContent>
    </AdminContainer>
  );
};

export default AdminSupportPage;
